/**
 * 2D grid reordering: which tile is under (or nearest) the pointer, and what
 * the array looks like after a move.
 *
 * Deliberately pure and geometry-only. Nothing here knows the column count, the
 * breakpoint, or how many rows there are — targets are resolved from the tiles'
 * MEASURED rectangles, exactly as a rect-based collision strategy would. That
 * is what makes up/down/diagonal identical to left/right, and what keeps the
 * logic correct when the grid rewraps at a different viewport width.
 */

/** A measured tile: its position in the array plus its viewport rectangle. */
export interface TileRect {
  index: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Squared distance from a point to a rectangle — zero when the point is inside.
 *
 * Squared, because only the ORDER of distances matters and skipping the square
 * root keeps this cheap enough to run for every tile on every pointer move.
 */
function distanceSq(rect: TileRect, x: number, y: number): number {
  const dx = Math.max(rect.left - x, 0, x - rect.right);
  const dy = Math.max(rect.top - y, 0, y - rect.bottom);
  return dx * dx + dy * dy;
}

/**
 * The tile a pointer at (x, y) is targeting.
 *
 * A hit inside a tile wins outright; otherwise the NEAREST tile by distance to
 * its rectangle wins. Falling back to "nearest" is what makes the whole surface
 * live: the gaps between rows and columns, the empty cells of a partially
 * filled last row, the strip below the grid, and anywhere the finger strays
 * outside it all resolve to the tile the user is plainly aiming at, instead of
 * leaving the drop target stuck on whatever it was last.
 *
 * Ties are real — the empty cell beside the last sticker of a half-filled final
 * row is exactly as far from that sticker as from the one above it. They are
 * broken toward the tile sharing the pointer's own row, so aiming at the space
 * after the last sticker means "put it at the end" rather than "put it in the
 * row above", and failing that toward the lower index, so the answer is stable
 * rather than flickering between two neighbours.
 */
export function nearestTileIndex(
  tiles: readonly TileRect[],
  x: number,
  y: number,
): number | null {
  let bestIndex: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestSameRow = false;
  for (const tile of tiles) {
    const distance = distanceSq(tile, x, y);
    if (distance === 0) return tile.index;
    const sameRow = y >= tile.top && y <= tile.bottom;
    const better =
      distance < bestDistance || (distance === bestDistance && sameRow && !bestSameRow);
    if (better) {
      bestDistance = distance;
      bestIndex = tile.index;
      bestSameRow = sameRow;
    }
  }
  return bestIndex;
}

/**
 * Move one item to another position, shifting everything between.
 *
 * The item is removed first and then inserted at `to`, so `to` means "the slot
 * currently occupied by the tile you are pointing at" in both directions — drag
 * #10 onto #3 and it becomes #3 with 3–9 shifting down; drag #2 onto #7 and it
 * becomes #7 with 3–7 shifting up. Out-of-range indices return the list
 * unchanged rather than corrupting it.
 */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) {
    return next;
  }
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}
